import { apiClient } from './client';
import type { RequestOptions } from '@/types/api';
import type {
  User,
  UserListResponse,
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  UserFilters,
} from '@/types/user';

/**
 * Users API functions for the CDN Manager
 * Admin-only endpoints for user management
 */
export const usersApi = {
  /**
   * Fetch all users (admin only)
   */
  async getAll(filters?: UserFilters, options?: RequestOptions): Promise<UserListResponse> {
    const params: Record<string, string> = {};

    if (filters?.role) {
      params.role = filters.role;
    }
    if (filters?.is_active !== undefined) {
      params.is_active = String(filters.is_active);
    }
    if (filters?.search) {
      params.search = filters.search;
    }
    if (filters?.limit) {
      params.limit = String(filters.limit);
    }
    if (filters?.offset) {
      params.offset = String(filters.offset);
    }

    const response = await apiClient.get<UserListResponse>('/users', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  /**
   * Fetch a single user by ID (admin only)
   */
  async getById(id: string): Promise<User> {
    const response = await apiClient.get<User>(`/users/${id}`);
    return response.data;
  },

  /**
   * Create a new user (admin only)
   */
  async create(data: CreateUserRequest): Promise<CreateUserResponse> {
    const response = await apiClient.post<CreateUserResponse>('/users', data);
    return response.data;
  },

  /**
   * Update an existing user (admin only)
   */
  async update(id: string, data: UpdateUserRequest): Promise<User> {
    const response = await apiClient.put<User>(`/users/${id}`, data);
    return response.data;
  },

  /**
   * Delete a user (admin only)
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },
};
